/* @flow */
import { Availability, Bundle } from "shared/src/types/articlesearch/Article";
import { TrackingTags } from "shared/src/tracking/Tracking";
import { SellingPriceInfo } from "shared/src/types/EvaluateArticleNew";
import { trackStructuredEvent, trackForGtmOnly } from "shared/src/utils/TrackingUtil";
import { Category } from "shared/src/types/articlesearch/Category";
import { BettyBundle } from "shared/src/types/EvaluateArticleNew";

export function priceInfoExtractor(priceAndPromotionInfo: SellingPriceInfo | undefined | null): {
    customerIndividualPrice: boolean | undefined | null;
    basePrice: number | undefined | null;
    deliveryPrice: number | undefined | null;
    isPromo: boolean;
} {
    if (!priceAndPromotionInfo) {
        return {
            isPromo: false,
            basePrice: null,
            deliveryPrice: null,
            customerIndividualPrice: false
        };
    }
    const availablePromotions = priceAndPromotionInfo?.availablePromotions ?? [];
    return {
        isPromo: availablePromotions ? availablePromotions.length > 0 : false,
        basePrice: priceAndPromotionInfo?.basePrice ?? null,
        deliveryPrice: priceAndPromotionInfo?.deliveryPrice ?? null,
        customerIndividualPrice: !!priceAndPromotionInfo?.customerIndividualPrice
    };
}

export function categoryIdsExtractor(selectedBundle: Bundle | BettyBundle): string | null {
    const categories = selectedBundle?.categories as Category[] | undefined;
    if (!categories) {
        return null;
    }

    const extractLevels = categories.map((cat: any) => cat?.levels ?? []);

    const flattened: Array<{ id: string }> = ([] as Array<{ id: string }>).concat(...extractLevels);

    return flattened.map((item) => item?.id ?? "").join(",");
}

export function displayUnitExtractor(selectedBundle: Bundle): string | undefined | null {
    if (!selectedBundle) {
        return null;
    }
    if (selectedBundle.weightPerPiece) {
        return "WEIGHT_PER_PIECE";
    } else {
        return selectedBundle.isWeightArticle || "UNKNOWN";
    }
}

export function trackOpenProductView(
    originEventId: string,
    bettyBundleId: string,
    trackingId: string,
    pageType: string,
    zeroResultType: string | undefined | null,
    inContainer: boolean | undefined | null,
    origin: string,
    marketplace: boolean,
    offerId: string | undefined | null,
    trackingInfo: TrackingTags,
    optionalTrackingTags?: Partial<TrackingTags>
) {
    const optionalTrackingTagsSanitized = optionalTrackingTags || {};

    const tags: TrackingTags = Object.assign({}, trackingInfo, {
        ...optionalTrackingTagsSanitized,
        originEventId,
        bettyBundleId,
        trackingId,
        pageType,
        zeroResultType,
        inContainer,
        origin,
        marketplace,
        offerId
    });
    trackStructuredEvent("article.open", { tags: tags });
}

export function trackShopLinkClick(
    originEventId: string,
    bettyBundleId: string,
    trackingId: string,
    shopId: string,
    pageType: string,
    zeroResultType: string | undefined | null,
    inContainer: boolean | undefined | null
) {
    const tags: TrackingTags = {
        originEventId,
        bettyBundleId,
        trackingId,
        shopId: shopId,
        pageType: pageType,
        zeroResultType: zeroResultType,
        inContainer: inContainer
    };
    trackStructuredEvent("article.openShopPage", { tags: tags });
}

export function trackNoArticleData(inContainer: boolean | undefined | null, bettyBundleId: string) {
    const tags: TrackingTags = { bettyBundleId, inContainer };
    trackStructuredEvent("article.noData", { tags: tags });
}

export function trackArticleView(
    eventId: string,
    currentBettyBundleId: string,
    trackingId: string,
    description: string | undefined,
    imageType: string,
    pageType: string,
    zeroResultType: string | undefined | null,
    inContainer: boolean | undefined | null,
    onList: boolean,
    recentlyOrdered: boolean,
    availability: Availability | undefined | null,
    marketplace: boolean,
    offerId: string | undefined | null,
    // created in the articleUtils
    trackingInfo: TrackingTags,
    replacementLinkShown: boolean,
    solrScore: number | null | undefined,
    childArticleIds?: string[] | null | undefined,
    rewardIds?: string[] | null,
    // Track current articleCard-layout: Is usually inferred from CustomerSettingsStore, but may be overwritten at any time
    layout?: string | null
) {
    const tags: Partial<TrackingTags> = Object.assign({}, trackingInfo, {
        eventId,
        bettyBundleId: currentBettyBundleId,
        trackingId,
        description,
        pageType,
        zeroResultType,
        favorite: onList || recentlyOrdered,
        onList,
        recentlyOrdered,
        image: imageType !== "fallback" && imageType !== "bundleFallback",
        inContainer,
        availability,
        marketplace,
        offerId,
        replacementLinkShown,
        childArticleIds,
        rewardIds,
        layout
    });
    tags.solrScore = solrScore;
    trackStructuredEvent("article.cardView", { tags: tags });
}

export function trackFreeProductHover(
    originEventId: string,
    currentBettyBundleId: string,
    trackingId: string,
    onList: boolean,
    recentlyOrdered: boolean,
    // created in the articleUtils
    trackingInfo: TrackingTags,
    numRewards: number,
    popoverShown: boolean
) {
    const tags = Object.assign({}, trackingInfo, {
        originEventId,
        bettyBundleId: currentBettyBundleId,
        favorite: onList || recentlyOrdered,
        onList,
        recentlyOrdered,
        trackingId,
        numRewards,
        popoverShown
    });
    trackStructuredEvent("article.freeProductHover", { tags: tags });
}

export function articleIdsToTrackingArticle(articleIds: Array<string | undefined | null>): Array<
    | {
          articleId: string;
          price: number | undefined | null;
          quantity: number | undefined | null;
      }
    | undefined
    | null
> {
    return (articleIds ?? []).map((articleId) =>
        articleId ? { articleId: articleId, price: null, quantity: null } : null
    );
}

// Makes sure to track article views on a given page once.
export class ArticleTrackContainer {
    _alreadyTrackedArticles: Set<string>;

    constructor() {
        this._alreadyTrackedArticles = new Set();
    }

    trackArticles(articleIds: Array<string | undefined | null>) {
        const newArticles = (articleIds ?? []).filter(
            (articleId) => !!articleId && !this._alreadyTrackedArticles.has(articleId)
        );
        if (newArticles.length > 0) {
            (newArticles ?? []).forEach((articleId) => articleId && this._alreadyTrackedArticles.add(articleId));
            const hiddenData = { articles: articleIdsToTrackingArticle(newArticles) };
            trackForGtmOnly("article.adwords", {}, hiddenData);
        }
    }
}
